import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, X, Save, Edit2, Trash2, UserPlus, Shield, Target, Award, DollarSign, Calendar, RefreshCw } from 'lucide-react';
import { usersAPI } from '../utils/api';
import axios from 'axios';
import constant from '../constants/constant';
import { SearchableCountrySelect, FilterCountrySelect, CountryBadge } from '../components/SearchableCountrySelect';

const BDManagement = () => {
  const token = sessionStorage.getItem('adminToken');
  const userinfo = React.useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  const userType = userinfo?.user_type || 'ADMIN';

  const [activeTab, setActiveTab] = useState('profiles'); // 'profiles' or 'performance'
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [users, setUsers] = useState([]);
  const [performances, setPerformances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // BD Account Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    user_type: 'BD',
    country: '',
    is_active: true,
    user_id: ''
  });

  // BD Target Modal
  const [isTargetModalOpen, setIsTargetModalOpen] = useState(false);
  const [targetForm, setTargetForm] = useState({
    bd_id: '',
    month: new Date().toISOString().substring(0, 7), // YYYY-MM
    target_amount: ''
  });
  const [targetLoading, setTargetLoading] = useState(false);

  const countries = [
    { name: 'India', flag: '🇮🇳' },
    { name: 'USA', flag: '🇺🇸' },
    { name: 'UK', flag: '🇬🇧' },
    { name: 'Canada', flag: '🇨🇦' },
    { name: 'Australia', flag: '🇦🇺' },
    { name: 'UAE', flag: '🇦🇪' },
  ];

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'profiles') {
        const response = await usersAPI.getUsers();
        setUsers(response.data || []);
      } else {
        const perfRes = await axios.get(`${constant.API_BASE_URL}/admin/business/bd/performance`, {
          headers: { Authorization: token }
        });
        setPerformances(perfRes.data?.data || []);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching BD data:', err);
      setError('Failed to load details. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const openAddModal = () => {
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: '',
      user_type: 'BD',
      country: '',
      is_active: true,
      user_id: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      user_type: 'BD',
      country: 'India',
      is_active: user.is_active ?? true,
      user_id: user.user_id || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { country, ...payload } = formData;
      payload.user_type = 'BD'; 

      if (payload.phone === '') delete payload.phone;
      if (payload.user_id === '') delete payload.user_id;

      if (editingUser) {
        if (!payload.password) delete payload.password;
        await usersAPI.updateUser(editingUser.id, payload);
      } else {
        await usersAPI.createUser(payload);
      }
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving BD user');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this BD?')) {
      try {
        await usersAPI.deleteUser(id);
        fetchData();
      } catch (err) {
        alert('Error deleting BD user');
      }
    }
  };

  // BD TARGETS
  const openTargetModal = (preselectedBdId = '') => {
    setTargetForm({
      bd_id: preselectedBdId,
      month: new Date().toISOString().substring(0, 7),
      target_amount: ''
    });
    setIsTargetModalOpen(true);
  };

  const handleSetTarget = async (e) => {
    e.preventDefault();
    if (!targetForm.bd_id || !targetForm.target_amount) return;
    setTargetLoading(true);
    try {
      await axios.post(`${constant.API_BASE_URL}/admin/business/bd/targets`, {
        bd_id: targetForm.bd_id,
        month: targetForm.month,
        target_amount: Number(targetForm.target_amount)
      }, {
        headers: { Authorization: token }
      });
      setIsTargetModalOpen(false);
      fetchData();
    } catch (err) {
      alert("Failed to set target: " + (err.response?.data?.message || err.message));
    } finally {
      setTargetLoading(false);
    }
  };

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    if (user.user_type !== 'BD') return false;
    if (countryFilter !== 'ALL' && (!user.country || user.country.toLowerCase() !== countryFilter.toLowerCase())) return false;
    const term = searchTerm.toLowerCase();
    return (
      (user.first_name?.toLowerCase().includes(term)) ||
      (user.last_name?.toLowerCase().includes(term)) ||
      (user.email?.toLowerCase().includes(term)) ||
      (user.country?.toLowerCase().includes(term)) ||
      (user.user_id?.toLowerCase().includes(term))
    );
  }) : [];

  const bdUsers = Array.isArray(users) ? users.filter(user => user.user_type === 'BD') : [];

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">BD Management System</h2>
          <p className="text-slate-500 mt-1">Set monthly goals, audit achievements, levels, and commissions for Business Development accounts.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 p-1 rounded-xl">
            <button 
              onClick={() => setActiveTab('profiles')}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'profiles' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              BD Profiles
            </button>
            <button 
              onClick={() => setActiveTab('performance')}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === 'performance' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Performance & Targets
            </button>
          </div>
          <button 
            onClick={fetchData}
            className="p-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 bg-pink-600 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition active:scale-95 text-sm"
          >
            <UserPlus size={18} /> Add BD
          </button>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder={activeTab === 'profiles' ? "Search BD profiles..." : "Search performance logs..."}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {activeTab === 'profiles' && (
              <div className="w-full sm:w-48">
                <FilterCountrySelect
                  value={countryFilter}
                  onChange={setCountryFilter}
                  placeholder="All Countries"
                />
              </div>
            )}
          </div>
          {activeTab === 'performance' && (
            <button 
              onClick={() => openTargetModal()}
              className="px-4 py-2 bg-slate-900 text-white hover:bg-slate-800 rounded-xl text-xs font-bold transition flex items-center gap-1.5"
            >
              <Target size={14} /> Assign Target Goal
            </button>
          )}
        </div>

        {/* Content Section */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Loading data...</p>
          </div>
        ) : error ? (
          <div className="p-20 text-center text-red-500">
            {error}
            <button onClick={fetchData} className="block mx-auto mt-4 text-pink-600 font-bold underline">Try Again</button>
          </div>
        ) : activeTab === 'profiles' ? (
          /* BD PROFILES TABLE */
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">BD Profile</th>
                  <th className="p-6">Contact Info</th>
                  <th className="p-6">Location</th>
                  <th className="p-6">BD Code</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50/50 transition duration-150 group">
                    <td className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-lg border-2 border-white shadow-sm shrink-0">
                          {user.first_name?.[0] || 'B'}{user.last_name?.[0] || ''}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 text-base">{user.first_name} {user.last_name}</p>
                          <p className="text-xs font-extrabold text-pink-600">XOXO ID: {user.xoxo_id || user.user_id || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6">
                      <p className="text-slate-600 font-semibold">{user.email}</p>
                      <p className="text-xs text-slate-400 mt-1">{user.phone || 'No phone'}</p>
                    </td>
                    <td className="p-6">
                      <CountryBadge country={user.country} />
                    </td>
                    <td className="p-6">
                      <span className="text-xs font-bold text-pink-600 bg-pink-50 px-3 py-1.5 rounded-lg border border-pink-100">
                        {user.user_id || 'N/A'}
                      </span>
                    </td>
                    <td className="p-6">
                      <span className={`
                        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide
                        ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                      `}>
                        <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openTargetModal(user.id)}
                          className="px-2.5 py-1.5 bg-slate-900 text-white hover:bg-slate-800 rounded-lg text-xs font-bold transition flex items-center gap-1"
                          title="Assign goal target"
                        >
                          <Target size={14} /> Set Target
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1.5 hover:bg-blue-50 text-gray-400 hover:text-blue-600 rounded-lg transition" title="Edit BD">
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-lg transition" title="Delete BD">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan="6" className="p-10 text-center text-slate-500">No BD profiles found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* BD PERFORMANCE & GOALS TABLE */
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">Business Developer</th>
                  <th className="p-6">Month</th>
                  <th className="p-6">Target Goal (Coins)</th>
                  <th className="p-6">Achieved Target (Coins)</th>
                  <th className="p-6">Agencies & Hosts</th>
                  <th className="p-6 text-right">Target Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {performances.map((perf) => {
                  const target = Number(perf.target_amount) || 0;
                  const achieved = Number(perf.achieved_amount) || 0;
                  const pct = target > 0 ? Math.round((achieved / target) * 100) : 0;
                  const agencies = perf.agencies || [];
                  
                  return (
                    <React.Fragment key={perf.bd_id || perf.id}>
                      <tr className="hover:bg-slate-50/50 transition">
                        <td className="p-6">
                          <p className="font-bold text-slate-800 text-base">{perf.first_name} {perf.last_name}</p>
                          <p className="text-xs font-extrabold text-pink-600">XOXO: {perf.xoxo_id || 'N/A'}</p>
                          <p className="text-[10px] text-slate-400">{perf.email}</p>
                        </td>
                        <td className="p-6 text-slate-600 font-semibold">
                          <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-xl w-fit">
                            <Calendar size={14} className="text-slate-500" /> {perf.month}
                          </div>
                        </td>
                        <td className="p-6 font-extrabold text-slate-700">
                          <div className="flex items-center gap-1.5">
                            <span>🪙</span>
                            <span>{target.toLocaleString('en-IN')}</span>
                          </div>
                        </td>
                        <td className="p-6 font-extrabold text-amber-600">
                          <div className="flex items-center gap-1.5">
                            <span>🪙</span>
                            <span>{achieved.toLocaleString('en-IN')}</span>
                          </div>
                        </td>
                        <td className="p-6">
                          <div className="space-y-1">
                            <span className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 font-bold px-2.5 py-1 rounded-lg text-xs">
                              {perf.agencies_count || 0} Agencies • {perf.total_hosts_count || 0} Hosts
                            </span>
                          </div>
                        </td>
                        <td className="p-6 text-right">
                          <div className="flex flex-col items-end gap-1.5">
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                              pct >= 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {pct}% Achieved
                            </span>
                            <div className="w-28 bg-slate-100 h-2 rounded-full overflow-hidden">
                              <div className="bg-gradient-to-r from-pink-500 to-amber-500 h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%` }}></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {agencies.length > 0 && (
                        <tr className="bg-slate-50/40">
                          <td colSpan="6" className="p-4 pl-12">
                            <div className="bg-white border border-slate-200/60 rounded-2xl p-4 shadow-sm">
                              <p className="text-xs font-black text-slate-700 uppercase tracking-wider mb-3">Agencies Under {perf.first_name}:</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {agencies.map((agency) => (
                                  <div key={agency.id} className="p-3 bg-slate-50/80 rounded-xl border border-slate-100 flex items-center justify-between">
                                    <div>
                                      <p className="font-bold text-slate-800 text-xs">{agency.agency_name}</p>
                                      <p className="text-[10px] text-slate-400">{agency.hosts_count} Active Hosts</p>
                                    </div>
                                    <div className="text-right">
                                      <span className="font-extrabold text-amber-600 text-xs">🪙 {Number(agency.agency_target_coins || 0).toLocaleString('en-IN')}</span>
                                      <p className="text-[9px] text-slate-400 font-bold uppercase">Target Coins</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {performances.length === 0 && (
                  <tr>
                    <td colSpan="6" className="p-10 text-center text-slate-500">No performance goals set for this period.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* BD Account Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <h3 className="text-xl font-bold text-slate-800">{editingUser ? 'Edit BD' : 'Add New BD'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 uppercase">First Name</label>
                    <input
                      required
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 uppercase">Last Name</label>
                    <input
                      required
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Email Address</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Password {editingUser && '(Leave blank to keep same)'}</label>
                  <input
                    required={!editingUser}
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Phone</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Country</label>
                  <SearchableCountrySelect
                    value={formData.country}
                    onChange={(val) => setFormData({...formData, country: val})}
                    className="mt-1"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">BD Code (User ID)</label>
                  <input
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleInputChange}
                    placeholder="e.g. BD123456 (Leave blank to auto-generate)"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded text-pink-600 focus:ring-pink-500 border-slate-300 cursor-pointer"
                  />
                  <label htmlFor="is_active" className="text-sm font-semibold text-slate-700 cursor-pointer select-none">
                    BD is Active
                  </label>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-6 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-8 py-2.5 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 shadow-lg shadow-slate-200 transition active:scale-95"
                  >
                    <Save size={18} /> {editingUser ? 'Save Changes' : 'Create BD'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set Monthly Target Modal */}
      {isTargetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Target size={20} className="text-pink-500" /> Assign Monthly Goal
              </h3>
              <button onClick={() => setIsTargetModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSetTarget} className="p-6 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Select Business Developer</label>
                <select
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition font-semibold"
                  value={targetForm.bd_id}
                  onChange={(e) => setTargetForm({ ...targetForm, bd_id: e.target.value })}
                >
                  <option value="">-- Choose BD user --</option>
                  {bdUsers.map((bd) => (
                    <option key={bd.id} value={bd.id}>{bd.first_name} {bd.last_name} ({bd.email})</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Target Month</label>
                <input
                  required
                  type="month"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition font-semibold"
                  value={targetForm.month}
                  onChange={(e) => setTargetForm({ ...targetForm, month: e.target.value })}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Target Revenue Amount ($)</label>
                <input
                  required
                  type="number"
                  placeholder="e.g. 50000"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition font-bold"
                  value={targetForm.target_amount}
                  onChange={(e) => setTargetForm({ ...targetForm, target_amount: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsTargetModalOpen(false)}
                  className="px-5 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={targetLoading}
                  className="px-6 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition active:scale-95 shadow-lg shadow-slate-200 disabled:opacity-50"
                >
                  {targetLoading ? 'Assigning...' : 'Assign Goal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BDManagement;
